import React, { useState, useMemo, useRef } from 'react';
import { 
  X, 
  Filter, 
  FileDown, 
  FileSpreadsheet, 
  Printer, 
  Search, 
  Calendar, 
  School, 
  Bus, 
  UserCheck, 
  UserX, 
  Phone, 
  HeartPulse, 
  MapPin, 
  RotateCcw, 
  CheckSquare, 
  Square, 
  SlidersHorizontal, 
  Eye, 
  Sparkles, 
  Download,
  Check,
  AlertCircle,
  HelpCircle,
  Users,
  GraduationCap
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { safeHtml2Canvas } from '../utils/safeHtml2Canvas';
import { generateStudentReportPDF } from '../utils/studentPdfGenerator';
import { Student, SchoolClass, AcademicYear } from '../types';
import { getStudentClassForYear, getStudentTransportForYear } from '../services/academicYearService';

export interface StudentAdvancedReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  students: Student[];
  classes: SchoolClass[];
  transportLines: string[];
  academicYears: AcademicYear[];
  selectedYear: string;
  currentYearName: string;
  preSelectedStudentIds?: string[];
  initialSearch?: string;
  initialClassFilter?: string;
  initialTransportFilter?: string;
}

export type ColumnKey = 
  | 'index'
  | 'fullName'
  | 'nationalId'
  | 'academicYear'
  | 'className'
  | 'transportName'
  | 'escortName'
  | 'birthDate'
  | 'age'
  | 'address'
  | 'fatherName'
  | 'fatherPhone'
  | 'motherName'
  | 'motherPhone'
  | 'healthNotes'
  | 'status'
  | 'enrollmentDate';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  defaultSelected: boolean;
  category: 'basic' | 'placement' | 'personal' | 'parents' | 'medical';
}

const AVAILABLE_COLUMNS: ColumnDef[] = [
  { key: 'index', label: '#', defaultSelected: true, category: 'basic' },
  { key: 'fullName', label: 'שם מלא', defaultSelected: true, category: 'basic' },
  { key: 'nationalId', label: 'תעודת זהות', defaultSelected: true, category: 'basic' },
  { key: 'className', label: 'כיתה', defaultSelected: true, category: 'placement' },
  { key: 'transportName', label: 'קו הסעה', defaultSelected: true, category: 'placement' },
  { key: 'escortName', label: 'מלווה', defaultSelected: false, category: 'placement' },
  { key: 'status', label: 'סטטוס פעילות', defaultSelected: true, category: 'placement' },
  { key: 'birthDate', label: 'תאריך לידה', defaultSelected: false, category: 'personal' },
  { key: 'age', label: 'גיל', defaultSelected: false, category: 'personal' },
  { key: 'address', label: 'כתובת / יישוב', defaultSelected: true, category: 'personal' },
  { key: 'fatherName', label: 'שם האב', defaultSelected: false, category: 'parents' },
  { key: 'fatherPhone', label: 'טלפון אב', defaultSelected: true, category: 'parents' },
  { key: 'motherName', label: 'שם האם', defaultSelected: false, category: 'parents' },
  { key: 'motherPhone', label: 'טלפון אם', defaultSelected: true, category: 'parents' },
  { key: 'healthNotes', label: 'הערות בריאותיות', defaultSelected: false, category: 'medical' },
  { key: 'academicYear', label: 'שנת לימודים', defaultSelected: false, category: 'placement' },
  { key: 'enrollmentDate', label: 'תאריך שיבוץ/רישום', defaultSelected: false, category: 'placement' },
];

export const StudentAdvancedReportModal: React.FC<StudentAdvancedReportModalProps> = ({
  isOpen,
  onClose,
  students,
  classes,
  transportLines,
  academicYears,
  selectedYear,
  currentYearName,
  preSelectedStudentIds = [],
  initialSearch = '',
  initialClassFilter = 'all',
  initialTransportFilter = 'all',
}) => {
  // Active Tab: 'filters' | 'columns' | 'preview'
  const [activeTab, setActiveTab] = useState<'filters' | 'columns' | 'preview'>('filters');

  // Filter States
  const [filterYear, setFilterYear] = useState<string>(selectedYear);
  const [targetScope, setTargetScope] = useState<'all' | 'selected_only'>(
    preSelectedStudentIds.length > 0 ? 'selected_only' : 'all'
  );
  const [searchTerm, setSearchTerm] = useState<string>(initialSearch);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active_only' | 'inactive_only' | 'enrolled_only' | 'unenrolled_only'>('all');
  const [selectedClasses, setSelectedClasses] = useState<string[]>(
    initialClassFilter !== 'all' ? [initialClassFilter] : []
  );
  const [transportMode, setTransportMode] = useState<'all' | 'with_transport' | 'no_transport' | 'specific'>(
    initialTransportFilter === '__NONE__' 
      ? 'no_transport' 
      : initialTransportFilter !== 'all' 
      ? 'specific' 
      : 'all'
  );
  const [specificTransportLine, setSpecificTransportLine] = useState<string>(
    initialTransportFilter !== 'all' && initialTransportFilter !== '__NONE__' ? initialTransportFilter : ''
  );
  const [escortFilter, setEscortFilter] = useState<'all' | 'with_escort' | 'without_escort'>('all');
  const [phoneFilter, setPhoneFilter] = useState<'all' | 'missing_any' | 'missing_both' | 'has_at_least_one' | 'has_both'>('all');
  const [healthFilter, setHealthFilter] = useState<'all' | 'with_notes' | 'without_notes'>('all');
  const [addressSearch, setAddressSearch] = useState<string>('');
  const [birthYearFilter, setBirthYearFilter] = useState<string>('all');

  // Selected Columns
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<ColumnKey[]>(
    AVAILABLE_COLUMNS.filter(c => c.defaultSelected).map(c => c.key)
  );

  // Export Loading States
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');
  const abortExportRef = useRef(false);

  const handleCloseModal = () => {
    abortExportRef.current = true;
    document.querySelectorAll('[data-pdf-temp="true"]').forEach(el => el.remove());
    setExportingPDF(false);
    setExportingExcel(false);
    setExportProgress('');
    onClose();
  };

  // Report Layout Settings
  const [reportTitle, setReportTitle] = useState<string>('דוח תלמידים מרוכז');
  const [includeSummaryHeader, setIncludeSummaryHeader] = useState<boolean>(true);
  const [pdfOrientation, setPdfOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [pdfPageSize, setPdfPageSize] = useState<'a4' | 'a3'>('a4');

  // Non-blocking notification banner state
  const [notification, setNotification] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);

  const showNotify = (text: string, type: 'error' | 'success' | 'info' = 'info') => {
    setNotification({ text, type });
    setTimeout(() => {
      setNotification(prev => (prev?.text === text ? null : prev));
    }, 4500);
  };

  // Preview Pagination
  const [previewPage, setPreviewPage] = useState(1);
  const PREVIEW_PAGE_SIZE = 15;

  // Available unique birth years
  const availableBirthYears = useMemo(() => {
    const years = new Set<string>();
    students.forEach(s => {
      if (s.birthDate) {
        const parts = s.birthDate.split('-');
        if (parts[0] && parts[0].length === 4) {
          years.add(parts[0]);
        }
      }
    });
    return Array.from(years).sort().reverse();
  }, [students]);

  // Available unique cities/neighborhoods from addresses
  const commonAddresses = useMemo(() => {
    const set = new Set<string>();
    students.forEach(s => {
      if (s.address && s.address.trim()) {
        const firstWord = s.address.trim().split(',')[0].split(' ')[0];
        if (firstWord.length > 2) set.add(firstWord);
      }
    });
    return Array.from(set).slice(0, 8);
  }, [students]);

  // Calculate age helper
  const calculateAge = (birthDateStr?: string): string => {
    if (!birthDateStr) return '-';
    try {
      const birth = new Date(birthDateStr);
      if (isNaN(birth.getTime())) return '-';
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age >= 0 ? `${age}` : '-';
    } catch {
      return '-';
    }
  };

  // Filter students based on advanced criteria
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      // 1. Target scope
      if (targetScope === 'selected_only' && preSelectedStudentIds.length > 0) {
        if (!preSelectedStudentIds.includes(student.id)) return false;
      }

      const activeYear = filterYear || selectedYear;
      const yearEnrollment = getStudentClassForYear(student, activeYear);
      const rawEnrollment = student.enrollments?.[activeYear];
      const isInactive = rawEnrollment?.active === false || student.active === false;
      const isEnrolledInYear = !!yearEnrollment;

      // 2. Status & Enrollment filter
      if (statusFilter === 'active_only') {
        if (isInactive) return false;
      } else if (statusFilter === 'inactive_only') {
        if (!isInactive) return false;
      } else if (statusFilter === 'enrolled_only') {
        if (!isEnrolledInYear || isInactive) return false;
      } else if (statusFilter === 'unenrolled_only') {
        if (isEnrolledInYear || isInactive) return false;
      }

      // 3. Class filter
      if (selectedClasses.length > 0) {
        const studentClassName = yearEnrollment?.className || rawEnrollment?.className || student.className || '';
        const studentClassId = yearEnrollment?.classId || rawEnrollment?.classId || student.classId || '';
        const matchesAnyClass = selectedClasses.some(cls => {
          if (cls === '__NONE__') return !studentClassName && !studentClassId;
          return cls === studentClassName || cls === studentClassId;
        });
        if (!matchesAnyClass) return false;
      }

      // 4. Transport filter
      const transportInfo = getStudentTransportForYear(student, activeYear);
      const hasTransport = Boolean(transportInfo.transportName && transportInfo.transportName.trim());
      if (transportMode === 'with_transport' && !hasTransport) return false;
      if (transportMode === 'no_transport' && hasTransport) return false;
      if (transportMode === 'specific' && specificTransportLine) {
        if (transportInfo.transportName !== specificTransportLine) return false;
      }

      // 5. Escort filter
      const hasEscort = Boolean(transportInfo.escortName && transportInfo.escortName.trim());
      if (escortFilter === 'with_escort' && !hasEscort) return false;
      if (escortFilter === 'without_escort' && hasEscort) return false;

      // 6. Parents phone filter
      const hasFather = Boolean(student.fatherPhone && student.fatherPhone.trim());
      const hasMother = Boolean(student.motherPhone && student.motherPhone.trim());
      if (phoneFilter === 'missing_any' && hasFather && hasMother) return false;
      if (phoneFilter === 'missing_both' && (hasFather || hasMother)) return false;
      if (phoneFilter === 'has_at_least_one' && !hasFather && !hasMother) return false;
      if (phoneFilter === 'has_both' && (!hasFather || !hasMother)) return false;

      // 7. Health notes filter
      const hasHealthNotes = Boolean(student.healthNotes && student.healthNotes.trim() && student.healthNotes.trim() !== 'אין' && student.healthNotes.trim() !== 'אין הערות');
      if (healthFilter === 'with_notes' && !hasHealthNotes) return false;
      if (healthFilter === 'without_notes' && hasHealthNotes) return false;

      // 8. Birth year filter
      if (birthYearFilter !== 'all') {
        if (!student.birthDate || !student.birthDate.startsWith(birthYearFilter)) return false;
      }

      // 9. Address search
      if (addressSearch.trim()) {
        const addrLower = (student.address || '').toLowerCase();
        if (!addrLower.includes(addressSearch.trim().toLowerCase())) return false;
      }

      // 10. Free search term
      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        const studentClassName = yearEnrollment?.className || student.className || '';
        const match = 
          student.fullName.toLowerCase().includes(term) ||
          student.nationalId.includes(term) ||
          (student.fatherName && student.fatherName.toLowerCase().includes(term)) ||
          (student.motherName && student.motherName.toLowerCase().includes(term)) ||
          (student.fatherPhone && student.fatherPhone.includes(term)) ||
          (student.motherPhone && student.motherPhone.includes(term)) ||
          (student.address && student.address.toLowerCase().includes(term)) ||
          studentClassName.toLowerCase().includes(term) ||
          (transportInfo.transportName && transportInfo.transportName.toLowerCase().includes(term));
        if (!match) return false;
      }

      return true;
    }).sort((a, b) => {
      const activeYear = filterYear || selectedYear;
      const aClass = getStudentClassForYear(a, activeYear)?.className || a.className || '';
      const bClass = getStudentClassForYear(b, activeYear)?.className || b.className || '';
      const classComp = aClass.localeCompare(bClass, 'he', { numeric: true });
      if (classComp !== 0) return classComp;
      return a.fullName.localeCompare(b.fullName, 'he', { sensitivity: 'base' });
    });
  }, [
    students,
    targetScope,
    preSelectedStudentIds,
    filterYear,
    selectedYear,
    statusFilter,
    selectedClasses,
    transportMode,
    specificTransportLine,
    escortFilter,
    phoneFilter,
    healthFilter,
    birthYearFilter,
    addressSearch,
    searchTerm
  ]);

  // Reset all filters to default
  const resetFilters = () => {
    setFilterYear(selectedYear);
    setTargetScope('all');
    setSearchTerm('');
    setStatusFilter('all');
    setSelectedClasses([]);
    setTransportMode('all');
    setSpecificTransportLine('');
    setEscortFilter('all');
    setPhoneFilter('all');
    setHealthFilter('all');
    setAddressSearch('');
    setBirthYearFilter('all');
  };

  // Presets for filters
  const applyFilterPreset = (preset: 'allActive' | 'unenrolled' | 'transport' | 'medical' | 'missingContacts') => {
    resetFilters();
    switch (preset) {
      case 'allActive':
        setStatusFilter('active_only');
        break;
      case 'unenrolled':
        setStatusFilter('unenrolled_only');
        break;
      case 'transport':
        setTransportMode('with_transport');
        break;
      case 'medical':
        setHealthFilter('with_notes');
        break;
      case 'missingContacts':
        setPhoneFilter('missing_any');
        break;
    }
  };

  // Presets for columns
  const applyColumnPreset = (preset: 'all' | 'compact' | 'contacts' | 'transport' | 'medical') => {
    switch (preset) {
      case 'all':
        setSelectedColumnKeys(AVAILABLE_COLUMNS.map(c => c.key));
        break;
      case 'compact':
        setSelectedColumnKeys(['index', 'fullName', 'nationalId', 'className', 'status']);
        break;
      case 'contacts':
        setSelectedColumnKeys(['index', 'fullName', 'className', 'fatherName', 'fatherPhone', 'motherName', 'motherPhone', 'address']);
        break;
      case 'transport':
        setSelectedColumnKeys(['index', 'fullName', 'className', 'transportName', 'escortName', 'address', 'fatherPhone']);
        break;
      case 'medical':
        setSelectedColumnKeys(['index', 'fullName', 'className', 'birthDate', 'age', 'healthNotes', 'fatherPhone', 'motherPhone']);
        break;
    }
  };

  // Toggle single column
  const toggleColumn = (key: ColumnKey) => {
    if (key === 'fullName') return; // Always keep name
    setSelectedColumnKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Resolve cell value for export & preview
  const getCellValue = (student: Student, index: number, colKey: ColumnKey): string => {
    const activeYear = filterYear || selectedYear;
    const yearEnrollment = getStudentClassForYear(student, activeYear);
    const rawEnrollment = student.enrollments?.[activeYear];
    const transportInfo = getStudentTransportForYear(student, activeYear);
    const isInactive = rawEnrollment?.active === false || student.active === false;

    switch (colKey) {
      case 'index':
        return `${index + 1}`;
      case 'fullName':
        return student.fullName || '';
      case 'nationalId':
        return student.nationalId || '';
      case 'academicYear': {
        const found = academicYears.find(y => y.id === activeYear);
        return found ? found.name : activeYear;
      }
      case 'className':
        return yearEnrollment?.className || rawEnrollment?.className || student.className || 'לא שובץ/ה';
      case 'transportName':
        return transportInfo.transportName || 'ללא הסעה';
      case 'escortName':
        return transportInfo.escortName || '-';
      case 'birthDate':
        return student.birthDate || '-';
      case 'age':
        return calculateAge(student.birthDate);
      case 'address':
        return student.address || '-';
      case 'fatherName':
        return student.fatherName || '-';
      case 'fatherPhone':
        return student.fatherPhone || '-';
      case 'motherName':
        return student.motherName || '-';
      case 'motherPhone':
        return student.motherPhone || '-';
      case 'healthNotes':
        return student.healthNotes || 'אין';
      case 'status':
        return isInactive ? 'לא פעיל/ה' : (yearEnrollment ? 'פעיל/ה משובץ/ת' : 'פעיל/ה טרם שובץ/ה');
      case 'enrollmentDate':
        return rawEnrollment?.enrolledAt ? new Date(rawEnrollment.enrolledAt).toLocaleDateString('he-IL') : '-';
      default:
        return '';
    }
  };

  // Active filters summary string
  const activeFiltersSummary = useMemo(() => {
    const parts: string[] = [];
    const activeYearObj = academicYears.find(y => y.id === (filterYear || selectedYear));
    if (activeYearObj) parts.push(`שנה: ${activeYearObj.name}`);

    if (targetScope === 'selected_only') {
      parts.push(`נבחרו: ${preSelectedStudentIds.length} תלמידים`);
    }

    if (statusFilter === 'active_only') parts.push('פעילים בלבד');
    if (statusFilter === 'inactive_only') parts.push('לא פעילים בלבד');
    if (statusFilter === 'enrolled_only') parts.push('משובצים בלבד');
    if (statusFilter === 'unenrolled_only') parts.push('טרם שובצו');

    if (selectedClasses.length > 0) {
      parts.push(`כיתות: ${selectedClasses.join(', ')}`);
    }

    if (transportMode === 'with_transport') parts.push('עם הסעה בלבד');
    if (transportMode === 'no_transport') parts.push('ללא הסעה');
    if (transportMode === 'specific' && specificTransportLine) parts.push(`הסעה: ${specificTransportLine}`);
    if (escortFilter === 'with_escort') parts.push('עם מלווה');

    if (phoneFilter === 'missing_any') parts.push('חסר טלפון הורה');
    if (healthFilter === 'with_notes') parts.push('בעלי הערות בריאותיות');
    if (birthYearFilter !== 'all') parts.push(`שנת לידה: ${birthYearFilter}`);
    if (addressSearch) parts.push(`כתובת: ${addressSearch}`);
    if (searchTerm) parts.push(`חיפוש: "${searchTerm}"`);

    return parts.length > 0 ? parts.join(' | ') : 'ללא סינון נוסף (כל התלמידים במאגר)';
  }, [
    filterYear,
    selectedYear,
    academicYears,
    targetScope,
    preSelectedStudentIds,
    statusFilter,
    selectedClasses,
    transportMode,
    specificTransportLine,
    escortFilter,
    phoneFilter,
    healthFilter,
    birthYearFilter,
    addressSearch,
    searchTerm
  ]);

  // Export to Excel handler
  const handleExportExcel = () => {
    if (filteredStudents.length === 0) {
      showNotify('אין תלמידים התואמים למסנן שנבחר להפקה', 'error');
      return;
    }

    setExportingExcel(true);
    setExportProgress('מייצר קובץ Excel...');

    try {
      const activeColumns = AVAILABLE_COLUMNS.filter(c => selectedColumnKeys.includes(c.key));
      const yearName = academicYears.find(y => y.id === (filterYear || selectedYear))?.name || (filterYear || selectedYear);

      // Create data rows
      const sheetData: any[][] = [];

      // Header Banner
      sheetData.push(['בית ספר אלמג\'ד']);
      sheetData.push([reportTitle || 'דוח תלמידים']);
      sheetData.push([`שנת לימודים: ${yearName}`, `תאריך הפקה: ${new Date().toLocaleDateString('he-IL')} ${new Date().toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`]);
      sheetData.push([`מסננים שהופעלו: ${activeFiltersSummary}`]);
      sheetData.push([`סה"כ תלמידים בדוח: ${filteredStudents.length}`]);
      sheetData.push([]); // Empty row before table

      // Table Header Row
      const tableHeaders = activeColumns.map(c => c.label);
      sheetData.push(tableHeaders);

      // Table Data Rows
      filteredStudents.forEach((student, idx) => {
        const row = activeColumns.map(col => getCellValue(student, idx, col.key));
        sheetData.push(row);
      });

      // Create Worksheet
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      // Auto-fit column widths
      const colWidths = activeColumns.map((col, colIdx) => {
        let maxLen = col.label.length;
        filteredStudents.forEach((student, rowIdx) => {
          const val = getCellValue(student, rowIdx, col.key);
          if (val && val.length > maxLen) {
            maxLen = Math.min(val.length, 40);
          }
        });
        return { wch: Math.max(maxLen + 3, 10) };
      });
      ws['!cols'] = colWidths;

      // Enable Right-to-Left sheet view
      ws['!views'] = [{ rightToLeft: true }];

      // Create Workbook and append sheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'דוח תלמידים');

      // Generate clean file name
      const dateStr = new Date().toISOString().split('T')[0];
      const safeTitle = (reportTitle || 'דוח_תלמידים').replace(/\s+/g, '_');
      const filename = `${safeTitle}_${yearName.replace(/[\s\/\(\)]+/g, '_')}_${dateStr}.xlsx`;

      XLSX.writeFile(wb, filename);
      showNotify('קובץ Excel יוצא בהצלחה!', 'success');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      showNotify('שגיאה בייצוא קובץ האקסל. אנא נסה שנית.', 'error');
    } finally {
      setExportingExcel(false);
      setExportProgress('');
    }
  };

  // Export to PDF handler
  const handleExportPDF = async () => {
    if (filteredStudents.length === 0) {
      showNotify('אין תלמידים התואמים למסנן שנבחר להפקה', 'error');
      return;
    }

    abortExportRef.current = false;
    setExportingPDF(true);
    setExportProgress('מפיק מסמך PDF להורדה...');

    try {
      const activeCols = AVAILABLE_COLUMNS.filter(c => selectedColumnKeys.includes(c.key));
      const yearName = academicYears.find(y => y.id === (filterYear || selectedYear))?.name || (filterYear || selectedYear);

      await generateStudentReportPDF(
        filteredStudents,
        activeCols,
        getCellValue,
        {
          reportTitle: reportTitle || 'דוח תלמידים מרוכז',
          yearName,
          filtersSummary: activeFiltersSummary,
          orientation: pdfOrientation,
          pageSize: pdfPageSize
        },
        (progressText) => {
          if (!abortExportRef.current) {
            setExportProgress(progressText);
          }
        }
      );

      showNotify('קובץ PDF הופק והורד בהצלחה!', 'success');
    } catch (err: any) {
      console.error('PDF export error:', err);
      showNotify('שגיאה בהפקת PDF: ' + (err?.message || 'אנא נסה שנית'), 'error');
    } finally {
      setExportingPDF(false);
      setExportProgress('');
    }
  };

  // Direct Browser Print via hidden iframe (never blocked by popup blockers)
  const handleDirectPrint = () => {
    if (filteredStudents.length === 0) {
      showNotify('אין תלמידים להדפסה', 'error');
      return;
    }

    const activeColumns = AVAILABLE_COLUMNS.filter(c => selectedColumnKeys.includes(c.key));
    const yearName = academicYears.find(y => y.id === (filterYear || selectedYear))?.name || (filterYear || selectedYear);

    const rowsHtml = filteredStudents.map((s, idx) => `
      <tr style="border-bottom: 1px solid #e2e8f0; ${idx % 2 === 1 ? 'background-color: #f8fafc;' : ''}">
        ${activeColumns.map(col => `
          <td style="padding: 8px 10px; font-size: 12px; text-align: right; ${col.key === 'fullName' ? 'font-weight: bold; color: #1e293b;' : 'color: #334155;'}">
            ${getCellValue(s, idx, col.key)}
          </td>
        `).join('')}
      </tr>
    `).join('');

    const headersHtml = activeColumns.map(col => `
      <th style="padding: 10px; background-color: #f1f5f9; color: #1e293b; font-size: 12px; font-weight: bold; text-align: right; border-bottom: 2px solid #cbd5e1;">
        ${col.label}
      </th>
    `).join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
        <head>
          <meta charset="utf-8">
          <title>${reportTitle} - ${yearName}</title>
          <style>
            @page {
              size: ${pdfOrientation === 'landscape' ? 'landscape' : 'portrait'};
              margin: 10mm;
            }
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
              direction: rtl;
              margin: 0;
              padding: 0;
              color: #0f172a;
            }
            .header-banner {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #e2e8f0;
              padding-bottom: 12px;
              margin-bottom: 14px;
            }
            .header-title {
              font-size: 20px;
              font-weight: 800;
              color: #1e1b4b;
              margin: 0;
            }
            .header-sub {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
            }
            .filter-tag {
              background: #f1f5f9;
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 11px;
              color: #475569;
              display: inline-block;
              margin-bottom: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 6px;
            }
            .footer-info {
              margin-top: 18px;
              text-align: center;
              font-size: 10px;
              color: #94a3b8;
              border-top: 1px solid #e2e8f0;
              padding-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="header-banner">
            <div>
              <h1 class="header-title">${reportTitle}</h1>
              <div class="header-sub">בית ספר אלמג'ד • שנת לימודים: ${yearName} • סה"כ תלמידים: ${filteredStudents.length}</div>
            </div>
            <div style="text-align: left;">
              <div style="font-weight: bold; font-size: 15px; color: #4338ca;">בית ספר אלמג'ד</div>
              <div style="font-size: 10px; color: #94a3b8; margin-top: 2px;">תאריך הפקה: ${new Date().toLocaleDateString('he-IL')}</div>
            </div>
          </div>

          <div class="filter-tag">
            <strong>מסננים:</strong> ${activeFiltersSummary}
          </div>

          <table>
            <thead>
              <tr>${headersHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="footer-info">
            הופק ממערכת ניהול אלמג'ד • עמוד הדפסה ושמירה כ-PDF
          </div>
        </body>
      </html>
    `;

    // Try hidden iframe print first (doesn't trigger popup blocker)
    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(htmlContent);
        doc.close();
        iframe.contentWindow?.focus();
        setTimeout(() => {
          try {
            iframe.contentWindow?.print();
          } catch (e) {
            console.warn('Iframe print error, falling back to window.open:', e);
          }
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1500);
        }, 300);
        return;
      }
    } catch (e) {
      console.warn('Could not print via iframe:', e);
      showNotify('לא ניתן להפעיל הדפסה אוטומטית בדפדפן זה.', 'error');
    }
  };

  if (!isOpen) return null;

  // Active columns objects
  const activeColumns = AVAILABLE_COLUMNS.filter(c => selectedColumnKeys.includes(c.key));
  const activeYearName = academicYears.find(y => y.id === (filterYear || selectedYear))?.name || (filterYear || selectedYear);

  // Pagination for preview
  const totalPages = Math.ceil(filteredStudents.length / PREVIEW_PAGE_SIZE) || 1;
  const paginatedStudents = filteredStudents.slice((previewPage - 1) * PREVIEW_PAGE_SIZE, previewPage * PREVIEW_PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/70 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 15 }}
        className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl border border-slate-100 flex flex-col max-h-[92vh] overflow-hidden text-right"
        dir="rtl"
      >
        {/* Top Header */}
        <div className="bg-gradient-to-l from-indigo-700 via-indigo-600 to-indigo-800 text-white p-5 sm:p-6 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md text-amber-300">
                <SlidersHorizontal size={26} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl sm:text-2xl font-black tracking-tight">הפקת דוח תלמידים מתקדם</h3>
                  <span className="bg-amber-400 text-amber-950 text-xs font-black px-2.5 py-0.5 rounded-full shadow-sm">
                    PDF & Excel
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-indigo-100 mt-1">
                  סינון רב-שכבתי מדויק, בחירת עמודות והורדת דוחות מעוצבים באקסל וב-PDF
                </p>
              </div>
            </div>

            <button
              onClick={handleCloseModal}
              className="p-2.5 rounded-2xl bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              title="סגור חלון"
            >
              <X size={20} />
            </button>
          </div>

          {/* Real-time Result Badge & Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-white/10">
            {/* Nav Tabs */}
            <div className="flex bg-black/20 p-1 rounded-2xl backdrop-blur-md text-xs font-bold gap-1">
              <button
                onClick={() => setActiveTab('filters')}
                className={`px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${
                  activeTab === 'filters' 
                    ? 'bg-white text-indigo-800 shadow-sm' 
                    : 'text-white/80 hover:text-white'
                }`}
              >
                <Filter size={16} />
                <span>1. הגדרת מסננים מתקדמים</span>
              </button>
              <button
                onClick={() => setActiveTab('columns')}
                className={`px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${
                  activeTab === 'columns' 
                    ? 'bg-white text-indigo-800 shadow-sm' 
                    : 'text-white/80 hover:text-white'
                }`}
              >
                <CheckSquare size={16} />
                <span>2. בחירת עמודות לדוח ({selectedColumnKeys.length})</span>
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-2 rounded-xl transition-all flex items-center gap-2 ${
                  activeTab === 'preview' 
                    ? 'bg-white text-indigo-800 shadow-sm' 
                    : 'text-white/80 hover:text-white'
                }`}
              >
                <Eye size={16} />
                <span>3. תצוגה מקדימה והגדרות פלט</span>
              </button>
            </div>

            {/* Live Count Pill */}
            <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 px-3.5 py-1.5 rounded-2xl text-xs font-bold">
              <Users size={16} className="text-emerald-300" />
              <span>נמצאו:</span>
              <span className="text-white font-black text-sm bg-emerald-600 px-2 py-0.5 rounded-lg shadow-sm">
                {filteredStudents.length}
              </span>
              <span>תלמידים תואמים</span>
            </div>
          </div>
        </div>

        {/* Modal Body - Tab Content */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-6 bg-slate-50/50">

          {/* TAB 1: ADVANCED FILTERS */}
          {activeTab === 'filters' && (
            <div className="space-y-6">
              {/* Quick Presets Bar */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                    <Sparkles size={16} className="text-amber-500" />
                    <span>מסננים מהירים בלחיצה אחת:</span>
                  </div>
                  <button
                    onClick={resetFilters}
                    className="text-xs font-bold text-slate-500 hover:text-red-600 flex items-center gap-1 transition-all"
                  >
                    <RotateCcw size={14} />
                    <span>איפוס כל המסננים</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => applyFilterPreset('allActive')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-700 text-xs font-bold transition-all border border-slate-200 hover:border-indigo-300"
                  >
                    פעילים בלבד לשנה זו
                  </button>
                  <button
                    onClick={() => applyFilterPreset('unenrolled')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-amber-50 hover:text-amber-800 text-slate-700 text-xs font-bold transition-all border border-slate-200 hover:border-amber-300"
                  >
                    תלמידים שטרם שובצו
                  </button>
                  <button
                    onClick={() => applyFilterPreset('transport')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-purple-50 hover:text-purple-700 text-slate-700 text-xs font-bold transition-all border border-slate-200 hover:border-purple-300"
                  >
                    תלמידי הסעות בלבד
                  </button>
                  <button
                    onClick={() => applyFilterPreset('medical')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-700 text-slate-700 text-xs font-bold transition-all border border-slate-200 hover:border-rose-300"
                  >
                    בעלי הערות בריאותיות
                  </button>
                  <button
                    onClick={() => applyFilterPreset('missingContacts')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 text-xs font-bold transition-all border border-slate-200 hover:border-blue-300"
                  >
                    חסר טלפון של אחד ההורים
                  </button>
                </div>
              </div>

              {/* Scope Selection (if students were checked in table) */}
              {preSelectedStudentIds.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 text-white rounded-xl">
                      <CheckSquare size={20} />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-amber-950">
                        סומנו מראש {preSelectedStudentIds.length} תלמידים בטבלה
                      </div>
                      <div className="text-xs text-amber-800">
                        תוכל להגביל את הדוח לתלמידים המסומנים בלבד או להחיל על כל המאגר
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setTargetScope('selected_only')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        targetScope === 'selected_only'
                          ? 'bg-amber-600 text-white shadow-sm'
                          : 'bg-white text-amber-900 border border-amber-300'
                      }`}
                    >
                      הגבל לנבחרים בלבד ({preSelectedStudentIds.length})
                    </button>
                    <button
                      onClick={() => setTargetScope('all')}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        targetScope === 'all'
                          ? 'bg-amber-600 text-white shadow-sm'
                          : 'bg-white text-amber-900 border border-amber-300'
                      }`}
                    >
                      החל על כלל המאגר ({students.length})
                    </button>
                  </div>
                </div>
              )}

              {/* Grid of Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                {/* 1. Academic Year */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <Calendar size={16} className="text-indigo-600" />
                    <span>שנת לימודים לשיבוץ</span>
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    {academicYears.map(y => (
                      <option key={y.id} value={y.id}>
                        {y.name} {y.id === selectedYear ? '(פעילה)' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400">
                    קובע לפי איזו שנת לימודים ייבדקו שיבוץ הכיתה וקו ההסעה
                  </p>
                </div>

                {/* 2. Status & Placement */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <UserCheck size={16} className="text-emerald-600" />
                    <span>סטטוס פעילות ושיבוץ</span>
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">כל התלמידים (פעילים ולא פעילים)</option>
                    <option value="active_only">פעילים בלבד לשנה זו</option>
                    <option value="enrolled_only">משובצים לכיתה לשנה זו בלבד</option>
                    <option value="unenrolled_only">טרם שובצו לכיתה לשנה זו</option>
                    <option value="inactive_only">לא פעילים בלבד לשנה זו</option>
                  </select>
                  <p className="text-[11px] text-slate-400">
                    מאפשר להפיק דוח למשובצים בלבד או לתלמידים שטרם סודרו
                  </p>
                </div>

                {/* 3. Free Text Search */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <Search size={16} className="text-indigo-600" />
                    <span>חיפוש חופשי (שם, ת״ז, טלפון, הערה)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="הקלד לסינון..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute left-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    מחפש בשם תלמיד, ת״ז, שמות וטלפוני הורים
                  </p>
                </div>

                {/* 4. Classes (Multi-select) */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <School size={16} className="text-indigo-600" />
                      <span>כיתות ספציפיות ({selectedClasses.length === 0 ? 'כל הכיתות' : selectedClasses.length})</span>
                    </span>
                    {selectedClasses.length > 0 && (
                      <button
                        onClick={() => setSelectedClasses([])}
                        className="text-[10px] text-indigo-600 hover:underline"
                      >
                        נקה בחירה
                      </button>
                    )}
                  </label>
                  <div className="max-h-36 overflow-y-auto space-y-1.5 p-2 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedClasses.length === 0}
                        onChange={() => setSelectedClasses([])}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                      />
                      <span>כל הכיתות (ללא סינון כיתה)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-amber-800 font-bold">
                      <input
                        type="checkbox"
                        checked={selectedClasses.includes('__NONE__')}
                        onChange={() => {
                          setSelectedClasses(prev => 
                            prev.includes('__NONE__') ? prev.filter(c => c !== '__NONE__') : [...prev, '__NONE__']
                          );
                        }}
                        className="w-4 h-4 rounded text-amber-600 focus:ring-amber-500 accent-amber-600"
                      />
                      <span>ללא כיתה משובצת</span>
                    </label>
                    {classes.map(c => {
                      const isChecked = selectedClasses.includes(c.name) || selectedClasses.includes(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer text-slate-700">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              setSelectedClasses(prev => 
                                isChecked ? prev.filter(x => x !== c.name && x !== c.id) : [...prev, c.name]
                              );
                            }}
                            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                          />
                          <span>{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* 5. Transport Lines */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <Bus size={16} className="text-purple-600" />
                    <span>מערך הסעות</span>
                  </label>
                  <select
                    value={transportMode}
                    onChange={(e) => setTransportMode(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">כל התלמידים (עם וללא הסעה)</option>
                    <option value="with_transport">תלמידים בעלי הסעה בלבד</option>
                    <option value="no_transport">תלמידים ללא הסעה בלבד</option>
                    <option value="specific">קו הסעה מסוים...</option>
                  </select>
                  {transportMode === 'specific' && (
                    <select
                      value={specificTransportLine}
                      onChange={(e) => setSpecificTransportLine(e.target.value)}
                      className="w-full bg-purple-50 border border-purple-200 rounded-xl px-3 py-2 text-xs font-bold text-purple-900 focus:ring-2 focus:ring-purple-500 outline-none mt-2"
                    >
                      <option value="">בחר קו הסעה...</option>
                      {transportLines.map(line => (
                        <option key={line} value={line}>{line}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* 6. Escort Filter */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <Users size={16} className="text-indigo-600" />
                    <span>מלווה בהסעה</span>
                  </label>
                  <select
                    value={escortFilter}
                    onChange={(e) => setEscortFilter(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">הכל (ללא תלות במלווה)</option>
                    <option value="with_escort">תלמידים עם מלווה בלבד</option>
                    <option value="without_escort">ללא מלווה בהסעה</option>
                  </select>
                  <p className="text-[11px] text-slate-400">
                    מצוין לצורך תיאום תקציבי ומעקב אחרי זכאות לליווי
                  </p>
                </div>

                {/* 7. Contact Details Status */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <Phone size={16} className="text-blue-600" />
                    <span>שלמות פרטי קשר (טלפון הורים)</span>
                  </label>
                  <select
                    value={phoneFilter}
                    onChange={(e) => setPhoneFilter(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">הכל (ללא בדיקת טלפונים)</option>
                    <option value="missing_any">חסר טלפון אב או אם</option>
                    <option value="missing_both">חסרים שני הטלפונים (ללא טלפון כלל)</option>
                    <option value="has_at_least_one">קיים טלפון של הורה אחד לפחות</option>
                    <option value="has_both">קיימים טלפונים של שני ההורים</option>
                  </select>
                  <p className="text-[11px] text-slate-400">
                    איתור מהיר של תלמידים שחסרים להם מספרי טלפון להשלמת תיק
                  </p>
                </div>

                {/* 8. Medical / Health Notes */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <HeartPulse size={16} className="text-rose-600" />
                    <span>הערות רפואיות / בריאותיות</span>
                  </label>
                  <select
                    value={healthFilter}
                    onChange={(e) => setHealthFilter(e.target.value as any)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">הכל (כולל עם ובלי הערות)</option>
                    <option value="with_notes">רק תלמידים עם הערות רפואיות/אלרגיות</option>
                    <option value="without_notes">רק תלמידים ללא הערות מיוחדות</option>
                  </select>
                  <p className="text-[11px] text-slate-400">
                    הפקת רשימת תלמידים בעלי רגישויות או הנחיות רפואיות
                  </p>
                </div>

                {/* 9. Address & Birth Year */}
                <div className="bg-white p-4 rounded-2xl border border-slate-200 space-y-2">
                  <label className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                    <MapPin size={16} className="text-emerald-600" />
                    <span>כתובת ושנת לידה</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="יישוב/רחוב..."
                      value={addressSearch}
                      onChange={(e) => setAddressSearch(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs text-slate-800 outline-none"
                    />
                    <select
                      value={birthYearFilter}
                      onChange={(e) => setBirthYearFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5 text-xs font-bold text-slate-800 outline-none"
                    >
                      <option value="all">כל השנים</option>
                      {availableBirthYears.map(yr => (
                        <option key={yr} value={yr}>שנת {yr}</option>
                      ))}
                    </select>
                  </div>
                  {commonAddresses.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {commonAddresses.map(addr => (
                        <button
                          key={addr}
                          onClick={() => setAddressSearch(addr)}
                          className="text-[10px] bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded text-slate-600"
                        >
                          {addr}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Active Filter Summary Bar */}
              <div className="p-3.5 bg-indigo-50/80 border border-indigo-100 rounded-2xl flex items-center justify-between gap-3 text-xs text-indigo-900">
                <div className="flex items-center gap-2">
                  <InfoIcon className="text-indigo-600 shrink-0" size={16} />
                  <span>
                    <strong>סיכום מסננים פעילים:</strong> {activeFiltersSummary}
                  </span>
                </div>
                <button
                  onClick={() => setActiveTab('columns')}
                  className="bg-indigo-600 text-white px-4 py-1.5 rounded-xl font-bold hover:bg-indigo-700 transition-all shrink-0"
                >
                  המשך לבחירת עמודות &larr;
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: CHOOSE COLUMNS */}
          {activeTab === 'columns' && (
            <div className="space-y-6">
              {/* Preset Column Buttons */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs font-bold text-slate-700">
                  ערכות עמודות מומלצות לפי צורך:
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => applyColumnPreset('all')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 text-xs font-bold border border-slate-200"
                  >
                    כל העמודות (דוח מלא)
                  </button>
                  <button
                    onClick={() => applyColumnPreset('compact')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 text-xs font-bold border border-slate-200"
                  >
                    מקוצר (שם, ת״ז, כיתה וסטטוס)
                  </button>
                  <button
                    onClick={() => applyColumnPreset('contacts')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 text-xs font-bold border border-slate-200"
                  >
                    דף קשר הורים
                  </button>
                  <button
                    onClick={() => applyColumnPreset('transport')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 text-xs font-bold border border-slate-200"
                  >
                    דוח מערך הסעות
                  </button>
                  <button
                    onClick={() => applyColumnPreset('medical')}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-indigo-50 text-slate-700 text-xs font-bold border border-slate-200"
                  >
                    דוח רפואי ובריאותי
                  </button>
                </div>
              </div>

              {/* Grid of Columns to Toggle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {AVAILABLE_COLUMNS.map(col => {
                  const isChecked = selectedColumnKeys.includes(col.key);
                  const isLocked = col.key === 'fullName';
                  return (
                    <div
                      key={col.key}
                      onClick={() => !isLocked && toggleColumn(col.key)}
                      className={`p-3.5 rounded-2xl border transition-all select-none flex items-center justify-between gap-3 cursor-pointer ${
                        isChecked 
                          ? 'bg-indigo-50/70 border-indigo-300 text-indigo-950 shadow-sm' 
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      } ${isLocked ? 'cursor-not-allowed opacity-90' : ''}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={() => {}}
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer"
                        />
                        <div>
                          <div className="text-xs font-bold">{col.label}</div>
                          <div className="text-[10px] text-slate-400 capitalize">
                            {col.category === 'basic' && 'זיהוי בסיסי'}
                            {col.category === 'placement' && 'שיבוץ ושנה'}
                            {col.category === 'personal' && 'פרטים אישיים'}
                            {col.category === 'parents' && 'קשר הורים'}
                            {col.category === 'medical' && 'בריאות'}
                          </div>
                        </div>
                      </div>
                      {isLocked && (
                        <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                          חובה
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Quick Actions at bottom of Tab 2 */}
              <div className="flex justify-between items-center pt-2">
                <div className="text-xs text-slate-500">
                  נבחרו <strong>{selectedColumnKeys.length}</strong> עמודות שיופיעו בדוח
                </div>
                <button
                  onClick={() => setActiveTab('preview')}
                  className="bg-indigo-600 text-white px-5 py-2 rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all"
                >
                  המשך לתצוגה מקדימה והפקה &larr;
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: PREVIEW & OUTPUT SETTINGS */}
          {activeTab === 'preview' && (
            <div className="space-y-6">
              {/* Output Settings Banner */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">
                    כותרת ראשית לדוח
                  </label>
                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">
                    כיוון דף להפקת PDF
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPdfOrientation('landscape')}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                        pdfOrientation === 'landscape'
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      דף לרוחב (מומלץ לריבוי עמודות)
                    </button>
                    <button
                      onClick={() => setPdfOrientation('portrait')}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                        pdfOrientation === 'portrait'
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      דף לאורך
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">
                    גודל דף
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPdfPageSize('a4')}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                        pdfPageSize === 'a4'
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      A4 סטנדרטי
                    </button>
                    <button
                      onClick={() => setPdfPageSize('a3')}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all border ${
                        pdfPageSize === 'a3'
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                    >
                      A3 רחב
                    </button>
                  </div>
                </div>
              </div>

              {/* Live Preview Table */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-bold text-slate-800">
                      תצוגה מקדימה של הנתונים ({filteredStudents.length} שורות)
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      עמוד {previewPage} מתוך {totalPages} • מוצגות {activeColumns.length} עמודות שנבחרו
                    </p>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                        disabled={previewPage <= 1}
                        className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      >
                        הקודם
                      </button>
                      <span className="text-xs font-bold text-slate-700 px-2">
                        {previewPage} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPreviewPage(p => Math.min(totalPages, p + 1))}
                        disabled={previewPage >= totalPages}
                        className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      >
                        הבא
                      </button>
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto max-h-96">
                  {filteredStudents.length === 0 ? (
                    <div className="p-12 text-center text-slate-400 space-y-2">
                      <AlertCircle size={36} className="mx-auto text-amber-500 opacity-80" />
                      <p className="font-bold text-sm text-slate-600">אין תלמידים התואמים למסנן שנבחר</p>
                      <p className="text-xs">נסה להרחיב את המסננים או לאפס אותם בטאב המסננים</p>
                    </div>
                  ) : (
                    <table className="w-full text-right text-xs border-collapse">
                      <thead className="bg-slate-100 sticky top-0 border-b border-slate-200 z-10">
                        <tr>
                          {activeColumns.map(col => (
                            <th key={col.key} className="p-3 font-bold text-slate-700 whitespace-nowrap border-l border-slate-200 last:border-l-0">
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {paginatedStudents.map((student, idx) => {
                          const realIdx = (previewPage - 1) * PREVIEW_PAGE_SIZE + idx;
                          return (
                            <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                              {activeColumns.map(col => (
                                <td key={col.key} className="p-3 text-slate-700 whitespace-nowrap border-l border-slate-100 last:border-l-0 font-medium">
                                  {getCellValue(student, realIdx, col.key)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Modal Footer with Primary Export Action Buttons */}
        <div className="p-4 sm:p-5 bg-white border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={handleCloseModal}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-all border border-slate-200 cursor-pointer"
            >
              סגור
            </button>
            <button
              onClick={handleDirectPrint}
              disabled={filteredStudents.length === 0}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-all border border-indigo-200 flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
              title="שמירה כ-PDF או הדפסה דרך חלון ההדפסה של הדפדפן"
            >
              <Printer size={16} />
              <span>הדפסה ושמירה כ-PDF</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* Export to Excel */}
            <button
              onClick={handleExportExcel}
              disabled={filteredStudents.length === 0 || exportingExcel || exportingPDF}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-md shadow-emerald-100 flex items-center gap-2 disabled:opacity-50"
            >
              <FileSpreadsheet size={18} />
              <span>{exportingExcel ? 'מייצר קובץ אקסל...' : 'ייצוא לאקסל (Excel)'}</span>
            </button>

            {/* Export to PDF */}
            <button
              onClick={handleExportPDF}
              disabled={filteredStudents.length === 0 || exportingExcel || exportingPDF}
              className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-md shadow-indigo-100 flex items-center gap-2 disabled:opacity-50"
            >
              <FileDown size={18} />
              <span>{exportingPDF ? 'מייצר דוח PDF...' : 'הורדת דוח PDF'}</span>
            </button>
          </div>
        </div>

        {/* Loading Progress Overlay */}
        {(exportingExcel || exportingPDF) && (
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-30 flex items-center justify-center p-4">
            <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4 border border-slate-100 max-w-sm text-center">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin shrink-0" />
              <div>
                <p className="font-bold text-slate-800 text-sm">{exportProgress || 'מעבד נתונים...'}</p>
                <p className="text-xs text-slate-500 mt-1">אנא המתן עד לסיום ההפקה וההורדה</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  abortExportRef.current = true;
                  document.querySelectorAll('[data-pdf-temp="true"]').forEach(el => el.remove());
                  setExportingPDF(false);
                  setExportingExcel(false);
                  setExportProgress('');
                }}
                className="mt-2 px-5 py-2.5 rounded-xl text-xs font-bold text-rose-700 hover:text-rose-900 bg-rose-50 hover:bg-rose-100 transition-all border border-rose-200 cursor-pointer"
              >
                ביטול פעולה
              </button>
            </div>
          </div>
        )}

      </motion.div>
    </div>
  );
};

// Internal info icon helper to avoid missing import
function InfoIcon({ className, size }: { className?: string; size?: number }) {
  return (
    <svg 
      className={className} 
      width={size || 16} 
      height={size || 16} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

export default StudentAdvancedReportModal;
